import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { CheckCircle2, X, AlertTriangle, Loader2, Calendar, ClipboardCheck } from 'lucide-react';
import { baseurl } from '../../baseurl';
import Swal from 'sweetalert2';

const MarkComplete = ({ submissionId, onCompleteSuccess }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [completionNotes, setCompletionNotes] = useState('');
  
  const adminToken = localStorage.getItem('adminToken');

  const handleMarkAsComplete = async () => {
    setIsSubmitting(true);
    try {
      // API call to update status to 'Completed' and set complete_at
      const res = await axios.post(`${baseurl}markProjectComplete`, {
        submissionId: submissionId,
        notes: completionNotes
      }, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });

      if (res.data.success) {
        setIsModalOpen(false);
        Swal.fire({
          icon: 'success',
          title: 'Project Completed!',
          text: 'The project has been successfully moved to the completed archive.',
          confirmButtonColor: '#4318FF'
        });
        if (onCompleteSuccess) onCompleteSuccess();
      }
    } catch (err) {
      console.error(err);
      Swal.fire({
        icon: 'error',
        title: 'Action Failed',
        text: 'Could not update project status. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Trigger Button */}
      <button 
        onClick={() => setIsModalOpen(true)} 
        className="btn btn-success rounded-pill px-4 shadow-sm d-flex align-items-center fw-bold"
        style={{ backgroundColor: '#05CD99', border: 'none' }}
      >
        <CheckCircle2 size={18} className="me-2" /> Mark Complete
      </button>

      {/* Custom Confirmation Modal */}
      {isModalOpen && (
        <div className="MC-modal-overlay">
          <div className="MC-modal-container">
            <div className="MC-modal-header">
              <div className="MC-warning-icon">
                <AlertTriangle size={24} color="#FFB547" />
              </div>
              <button className="MC-close-x" onClick={() => setIsModalOpen(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="MC-modal-body text-center px-4">
              <h4 className="fw-800 text-dark mb-2">Finalize Project?</h4>
              <p className="text-muted small mb-4">
                You are about to mark Project <span className="fw-bold text-primary">#{submissionId}</span> as complete. 
                This will notify the user and archive the measurements.
              </p>

              <div className="MC-info-card mb-4">
                <div className="d-flex align-items-center mb-2">
                  <Calendar size={14} className="me-2 text-primary" />
                  <span className="small fw-bold text-dark">Completion Date: {new Date().toLocaleDateString()}</span>
                </div>
                <div className="d-flex align-items-center">
                  <ClipboardCheck size={14} className="me-2 text-primary" />
                  <span className="small fw-bold text-dark">Status Change: Pending → Completed</span>
                </div>
              </div>

              <div className="text-start mb-4">
                <label className="xs-label fw-bold text-muted mb-1">COMPLETION NOTES (OPTIONAL)</label>
                <textarea 
                  className="form-control MC-textarea" 
                  rows="3" 
                  placeholder="Add any final remarks for the client..."
                  value={completionNotes}
                  onChange={(e) => setCompletionNotes(e.target.value)}
                ></textarea>
              </div>
            </div>

            <div className="MC-modal-footer d-flex gap-3 px-4 pb-4">
              <button 
                className="btn btn-light rounded-pill flex-grow-1 fw-bold" 
                onClick={() => setIsModalOpen(false)}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary rounded-pill flex-grow-1 fw-bold d-flex align-items-center justify-content-center"
                style={{ backgroundColor: '#4318FF' }}
                onClick={handleMarkAsComplete}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <Loader2 size={18} className="animate-spin me-2" />
                ) : (
                  <CheckCircle2 size={18} className="me-2" />
                )}
                Confirm Completion
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .MC-modal-overlay {
          position: fixed; top: 0; left: 0; width: 100%; height: 100%;
          background: rgba(27, 37, 89, 0.4); backdrop-filter: blur(8px);
          display: flex; align-items: center; justify-content: center; z-index: 3000;
        }
        .MC-modal-container {
          background: white; width: 90%; max-width: 420px;
          border-radius: 30px; overflow: hidden;
          box-shadow: 0 25px 50px -12px rgba(0,0,0,0.2);
          animation: MC-popIn 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }
        .MC-modal-header {
          display: flex; justify-content: space-between; align-items: center;
          padding: 20px 20px 10px;
        }
        .MC-warning-icon {
          width: 50px; height: 50px; background: #FFF9F0;
          border-radius: 15px; display: flex; align-items: center; justify-content: center;
        }
        .MC-close-x {
          background: #F4F7FE; border: none; border-radius: 10px; padding: 5px; color: #A3AED0;
        }
        .MC-info-card {
          background: #F4F7FE; border-radius: 15px; padding: 15px; text-align: left;
        }
        .xs-label { font-size: 10px; letter-spacing: 0.5px; }
        .MC-textarea {
          border-radius: 12px; border: 2px solid #E0E5F2; font-size: 13px; resize: none;
        }
        .MC-textarea:focus {
          border-color: #4318FF; box-shadow: none;
        }
        .fw-800 { font-weight: 800; }
        .animate-spin { animation: spin 1s linear infinite; }
        
        @keyframes MC-popIn {
          from { transform: scale(0.9); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </>
  );
};

export default MarkComplete;